import React, { useState, useContext } from 'react';
import { Box, Typography, IconButton, InputAdornment, TextField, Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';

import { size } from 'lodash';
import Loading from '../components/Loading';
import { DataContext } from '../context/DataContext';
import { apiLogin } from '../api/Account';
import { getAppTheme } from '../theme';

function Copyright({ title }) {
	return (
		<Typography sx={{ fontSize: 11.5, color: 'text.disabled', textAlign: 'center', marginTop: '26px' }}>
			{`Copyright © ${title} ${new Date().getFullYear()}.`}
		</Typography>
	);
}

const Login = () => {
	const [showPassword, setShowPassword] = useState(false);
	const [userName, setUserName] = useState('');
	const [pass, setPass] = useState('');
	const [loginError, setLoginError] = useState('');

	const { setIsLoading, isDarkMode, setIsDarkMode, setIsLogged, setAccess, title, slogan, icon, setIsServerOff } =
		useContext(DataContext);

	const validar = () => {
		if (!userName.trim()) {
			setLoginError('Debe ingresar su usuario');
			return false;
		}

		if (!pass.trim()) {
			setLoginError('Debe ingresar su contraseña');
			return false;
		}

		if (size(pass) < 6) {
			setLoginError('la contraseña es menor de 6 caracteres');
			return false;
		}
		return true;
	};

	const iniciarSesion = async (event) => {
		event.preventDefault();
		setLoginError('');
		if (!validar()) {
			return;
		}
		setIsLoading(true);
		const data = {
			userName: userName,
			password: pass
		};
		const result = await apiLogin(data);
		if (!result.statusResponse) {
			setLoginError(result.error);
			setIsLoading(false);
			return;
		}

		setIsServerOff(result.data.userRol.isOff);
		setIsDarkMode(result.data.isDarkMode);
		setAccess(result.data.userRol.permissions);
		setIsLogged(true);
		setUserName('');
		setPass('');
		setIsLoading(false);
	};

	const tema = getAppTheme(isDarkMode);

	return (
		<ThemeProvider theme={tema}>
			<Box sx={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', backgroundColor: 'background.default' }}>
				<Box
					sx={{
						flex: 1.1,
						background: 'linear-gradient(150deg,#0f1b2d,#16294a)',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '60px 64px',
						gap: '20px'
					}}
				>
					<Box
						sx={{
							width: 160,
							height: 160,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
					>
						{icon ? (
							<Box component="img" src={icon} alt="logo" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
						) : (
							<SavingsRoundedIcon sx={{ fontSize: 96, color: '#00a152' }} />
						)}
					</Box>
					<Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
						<Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 40, letterSpacing: '-.4px' }}>{title}</Typography>
						<Typography sx={{ fontSize: 18, color: '#9fb0c3', fontWeight: 500, marginTop: '10px' }}>{slogan}</Typography>
					</Box>
				</Box>

				<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', position: 'relative' }}>
					<IconButton
						onClick={() => setIsDarkMode(!isDarkMode)}
						title="Cambiar tema"
						sx={{
							position: 'absolute',
							top: 22,
							right: 22,
							width: 38,
							height: 38,
							borderRadius: '9px',
							border: '1px solid',
							borderColor: 'divider',
							backgroundColor: 'background.paper',
							'&:hover': { borderColor: '#00a152' }
						}}
					>
						{isDarkMode ? <LightModeRoundedIcon sx={{ fontSize: 19 }} /> : <DarkModeRoundedIcon sx={{ fontSize: 19 }} />}
					</IconButton>

					<Box component="form" onSubmit={iniciarSesion} sx={{ width: '100%', maxWidth: 380 }}>
						<Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary', letterSpacing: '-.3px' }}>
							Iniciar sesión
						</Typography>
						<Typography sx={{ fontSize: 13, color: 'text.secondary', marginTop: '5px', marginBottom: '26px' }}>
							Ingresa tus credenciales para continuar
						</Typography>

						{loginError ? (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '9px',
									backgroundColor: 'error.light',
									color: 'error.main',
									borderRadius: '9px',
									padding: '10px 13px',
									fontSize: 12.5,
									fontWeight: 500,
									marginBottom: '16px'
								}}
							>
								<ErrorRoundedIcon sx={{ fontSize: 18 }} />
								{loginError}
							</Box>
						) : null}

						<Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', marginBottom: '7px' }}>Usuario</Typography>
						<TextField
							fullWidth
							onChange={(e) => setUserName(e.target.value)}
							value={userName}
							placeholder="Ingrese su usuario"
							sx={{ marginBottom: '16px' }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<PersonRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
									</InputAdornment>
								)
							}}
						/>

						<Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', marginBottom: '7px' }}>Contraseña</Typography>
						<TextField
							fullWidth
							type={showPassword ? 'text' : 'password'}
							onChange={(e) => setPass(e.target.value)}
							value={pass}
							placeholder="Ingrese su contraseña"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton onClick={() => setShowPassword(!showPassword)} aria-label="toggle password visibility" edge="end">
											{showPassword ? (
												<VisibilityRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
											) : (
												<VisibilityOffRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
											)}
										</IconButton>
									</InputAdornment>
								)
							}}
						/>

						<Button
							type="submit"
							variant="contained"
							color="primary"
							fullWidth
							startIcon={<LoginRoundedIcon />}
							sx={{ marginTop: '24px', padding: '13px', fontSize: 14 }}
						>
							Iniciar sesión
						</Button>

						<Copyright title={title} />
					</Box>
				</Box>
			</Box>

			<Loading />
		</ThemeProvider>
	);
};

export default Login;
